import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type HolidaysConstructor = typeof import('date-holidays').default;
type HolidaysInstance = InstanceType<HolidaysConstructor>;

let holidaysCtor: HolidaysConstructor | null = null;

async function getHolidaysConstructor(): Promise<HolidaysConstructor> {
  if (!holidaysCtor) {
    const { default: Holidays } = await import('date-holidays');
    holidaysCtor = Holidays;
  }
  return holidaysCtor;
}

/** Loads the date-holidays chunk; safe to call multiple times. */
export async function preloadBusinessDaysHolidays(): Promise<void> {
  await getHolidaysConstructor();
}

const checkBusinessDay = (date: Date, hd: HolidaysInstance): boolean =>
  ![0, 6].includes(date.getUTCDay()) && // weekends
  !hd.isHoliday(date);

const createHolidayLookup = (country: string): HolidaysInstance => {
  if (!holidaysCtor) {
    throw new Error('Business days module not loaded');
  }
  return new holidaysCtor(country, {
    types: ['bank', 'public'],
    timezone: 'UTC',
  });
};

export const isBusinessDay = async ({
  date,
  country = 'CA',
}: {
  date: Date;
  country: string;
}): Promise<boolean> => {
  if (!date) return false;
  await getHolidaysConstructor();
  const hd = createHolidayLookup(country);
  return checkBusinessDay(date, hd);
};

export const isBusinessDayAndAllowWeekend = async ({
  date,
  country = 'CA',
}: {
  date: Date;
  country: string;
}): Promise<boolean> => {
  if (!date) return false;
  await getHolidaysConstructor();
  const hd = createHolidayLookup(country);
  return !hd.isHoliday(date);
};

/** For DatePicker `shouldDisableDate` after `preloadBusinessDaysHolidays()` has resolved. */
export const isBusinessDaySync = ({
  date,
  country = 'CA',
}: {
  date: Date;
  country: string;
}): boolean => {
  if (!date) return false;
  if (!holidaysCtor) {
    return ![0, 6].includes(date.getUTCDay());
  }
  const hd = createHolidayLookup(country);
  return checkBusinessDay(date, hd);
};

/** For DatePicker `shouldDisableDate` after `preloadBusinessDaysHolidays()` has resolved. */
export const isBusinessDayAndAllowWeekendSync = ({
  date,
  country = 'CA',
}: {
  date: Date;
  country: string;
}): boolean => {
  if (!date) return false;
  if (!holidaysCtor) {
    return true;
  }
  const hd = createHolidayLookup(country);
  return !hd.isHoliday(date);
};

const addOrSubtractBusinessDays = async (
  date: Date,
  action: 'add' | 'subtract',
  days: number,
  country = 'CA',
): Promise<Date> => {
  await getHolidaysConstructor();
  const hd = createHolidayLookup(country);
  let daysToIterate = days;
  let day = dayjs.utc(date);

  while (daysToIterate > 0) {
    day = day[action](1, 'day');
    if (checkBusinessDay(day.toDate(), hd)) {
      daysToIterate -= 1;
    }
  }

  while (!checkBusinessDay(day.toDate(), hd)) {
    day = day.add(1, 'day');
  }

  return day.toDate();
};

export const addBusinessDays = async (
  date: Date,
  days: number,
  country = 'CA',
): Promise<Date> => addOrSubtractBusinessDays(date, 'add', days, country);

export const subtractBusinessDays = async (
  date: Date,
  days: number,
  country = 'CA',
): Promise<Date> => addOrSubtractBusinessDays(date, 'subtract', days, country);
